import React, { useMemo } from 'react'
import { ApolloClient, ApolloLink, ApolloProvider, HttpLink, InMemoryCache } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import deepmerge from 'deepmerge'
import isEqual from 'lodash/isEqual'
import type { DefaultContext } from '@apollo/client'
import type { ErrorResponse } from '@apollo/client/link/error'

export const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__'

let clientInstance: ApolloClient<any>

export type OnErrorErrors = ErrorResponse
export type ClientTokenResult = {
  token: string
  expires_at: number // Unix timestamp in seconds
}
export type ClientOptions = {
  clientName?: string
  uri?: string
  uriPublic?: string
  uriProxy?: string
  'x-forwarded-for'?: string
  getHeaders?: (prevContext: DefaultContext) => Promise<object>
  getClientToken?: () => Promise<ClientTokenResult>
  onError?: (errors: OnErrorErrors) => void
  connectToDevTools?: boolean
}

const createClientDefaultOptions = {
  uri: undefined,
}

export const createClient = (options: ClientOptions = createClientDefaultOptions) => {
  const httpLink = new HttpLink({
    uri: options.uri,
    credentials: 'same-origin', // Additional fetch() options like `credentials` or `headers`
    headers: {
      ...(options['x-forwarded-for'] && {
        'x-forwarded-for': options['x-forwarded-for'],
      }),
    },
  })

  const httpLinkPublic = new HttpLink({
    uri: options.uriPublic,
    credentials: 'same-origin', // Additional fetch() options like `credentials` or `headers`
    headers: {
      ...(options['x-forwarded-for'] && {
        'x-forwarded-for': options['x-forwarded-for'],
      }),
    },
  })

  const httpLinkProxy = new HttpLink({
    uri: options.uriProxy,
    credentials: 'same-origin', // Additional fetch() options like `credentials` or `headers`
    headers: {
      ...(options['x-forwarded-for'] && {
        'x-forwarded-for': options['x-forwarded-for'],
      }),
    },
  })

  const linkSplit = ApolloLink.split(
    (operation) => operation.getContext().clientName === 'public',
    httpLinkPublic, // <= apollo will send to this if clientName is "public"
    // httpLink // <= otherwise will send to this
    ApolloLink.split(
      (operation) => operation.getContext().clientName === 'proxy',
      httpLinkProxy, // <= apollo will send to this if clientName is "public"
      httpLink // <= otherwise will send to this
    )
  )

  // If getHeaders function exists, use it to populate request headers (for use with auth)
  const headersLink = setContext(async (_, prevContext) => {
    const { accessToken, additionalHeaders, clientName } = prevContext
    const isPublicReq = clientName === 'public'
    const isDirectReq = clientName === 'direct'

    let headers: object | undefined
    let directToken: string | undefined

    if (isPublicReq) {
      headers = undefined
    } else if (isDirectReq && options.getClientToken) {
      const tokenResult = await options.getClientToken()
      directToken = tokenResult.token
    } else if (options.getHeaders) {
      headers = await options.getHeaders(prevContext)
    }

    return {
      headers: {
        ...prevContext.headers,
        ...(directToken ? { authorization: `Bearer ${directToken}` } : {}),
        ...(accessToken && !isDirectReq ? { authorization: `Bearer ${accessToken}` } : {}),
        ...headers,
        ...generateClientSideHeaders(),
        ...additionalHeaders,
      },
    }
  })

  const ForwardUiConfigExtensionsLink = new ApolloLink((operation, forward) => {
    // Called after server responds
    return forward(operation).map((response) => {
      if (response.data && response.extensions) {
        response.data.uiConfig = response.extensions.uiConfig
      }
      return response
    })
  })

  const errorLink = onError(({ graphQLErrors, networkError, ...rest }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) =>
        console.log(`[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}`)
      )
    }
    if (networkError) {
      console.log(`[Network error]: ${networkError}`)
    }
    if (options.onError) {
      options.onError({ graphQLErrors, networkError, ...rest })
    }
  })

  const defaultOptions = {
    watchQuery: {
      fetchPolicy: 'cache-and-network' as const,
      errorPolicy: 'none' as const,
    },
    query: {
      fetchPolicy: 'network-only' as const,
      errorPolicy: 'none' as const,
    },
    mutate: {
      fetchPolicy: 'no-cache' as const,
      errorPolicy: 'none' as const,
    },
  }

  return new ApolloClient({
    name: options.clientName || 'phoenix-web',
    uri: options.uri,
    cache: new InMemoryCache(),
    ssrMode: typeof window === 'undefined',
    link: ApolloLink.from([headersLink, errorLink, ForwardUiConfigExtensionsLink, linkSplit]),
    defaultOptions,
    connectToDevTools: options.connectToDevTools ?? false,
  })
}

export function initializeApollo(options: ClientOptions, initialState = null) {
  const _apolloClient = clientInstance ?? createClient(options)

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state gets hydrated here
  if (initialState) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = _apolloClient.extract()

    // Merge the existing cache into data passed from getStaticProps/getServerSideProps
    const data = deepmerge(initialState, existingCache, {
      // combine arrays using object equality (like in sets)
      arrayMerge: (destinationArray: any, sourceArray: any) => [
        ...sourceArray,
        ...destinationArray.filter((d: any) => sourceArray.every((s: any) => !isEqual(d, s))),
      ],
    })

    // Restore the cache with the merged data
    _apolloClient.cache.restore(data)
  }
  // For SSG and SSR always create a new Apollo Client
  if (typeof window === 'undefined') return _apolloClient
  // Create the Apollo Client once in the client
  if (!clientInstance) clientInstance = _apolloClient

  return _apolloClient
}

export const resetStore = () => {
  clientInstance?.resetStore()
}

export function useApollo(options: ClientOptions, pageProps: Record<string, any>) {
  const state = useMemo(() => pageProps[APOLLO_STATE_PROP_NAME], [])
  const hasOptions = useMemo(() => !!options, []) // only care about first valid instance
  const opts = useMemo(() => options, [hasOptions])
  const store = useMemo(() => initializeApollo(opts, state), [state, opts])
  return store
}

export function addApolloState(client: typeof clientInstance, pageProps: Record<string, any>) {
  if (pageProps?.props) {
    pageProps.props[APOLLO_STATE_PROP_NAME] = client.cache.extract()
  }

  return pageProps
}

export const GraphqlProvider = ({
  clientOptions,
  children,
  pageProps,
}: {
  clientOptions: ClientOptions
  children: React.ReactElement
  pageProps: Record<string, any>
}) => {
  const client = useApollo(clientOptions, pageProps)
  return <ApolloProvider client={client}>{children}</ApolloProvider>
}

const generateClientSideHeaders = () => {
  const heapSessionId = typeof window !== 'undefined' ? window?.heap?.getSessionId?.() : undefined
  const heapUserId = typeof window !== 'undefined' ? window.heap?.getUserId?.() || window.heap?.userId : undefined
  return {
    ...(heapSessionId && { 'x-heap-session-id': heapSessionId }),
    ...(heapUserId && { 'x-heap-user-id': heapUserId }),
  }
}
